import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable café-wizard shell: a titled, mobile-first stepper with a progress
 * strip and a Back / Next(Play) footer. Presentation only — each template
 * wizard owns its steps + submit and drives this via props.
 *
 * ⚠️ RESTYLED (2026-09), NOT RECHECKED — markup/CSS ported verbatim from
 * .claude/fastcreate-prototype.html's stepBar()/foot. This component is pure
 * chrome (props in, no state, no API calls), so re-skinning it changes
 * nothing about what any wizard DOES — Match's behavior is exactly what it
 * was before this pass, just wearing the same visual language as the rest
 * of Fast Create now.
 *
 * Props:
 *   icon       : emoji / node shown by the title
 *   title      : wizard title (e.g. "Café Vibes")
 *   subtitle   : short helper line
 *   steps      : string[]  step labels, in order
 *   stepIndex  : number    current step (0-based)
 *   canNext    : boolean   enable the Next/Play button
 *   nextLabel  : string    label for the primary button on the LAST step
 *   submitting : boolean   spinner + disable while the final action runs
 *   onBack     : () => void   (also used to leave step 0 → back to picker)
 *   onNext     : () => void   advance, or submit on the last step
 *   onStepClick: (i) => void  optional — jump to a completed/current step
 *   children   : the current step's body
 */
export default function WizardShell({
  icon, title, subtitle, steps = [], stepIndex = 0,
  canNext = true, nextLabel, submitting = false,
  onBack, onNext, onStepClick, children,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.split('-')[0] || 'en';
  const fmtNumber = (n) => { try { return new Intl.NumberFormat(lng === 'ar' ? 'ar-EG' : 'en-US').format(n); } catch { return String(n); } };
  const isLast = stepIndex >= steps.length - 1;

  return (
    <div className="fc-wizard">
      <div className="fc-shell">
        {/* Header */}
        <div className="d-flex align-items-center gap-2 mb-1">
          <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{icon}</span>
          <div className="min-w-0">
            <h2 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 700 }}>{title}</h2>
            {subtitle && <div className="small text-muted text-truncate">{subtitle}</div>}
          </div>
        </div>

        {/* Progress strip — ported from stepBar() */}
        <div className="fc-steps" role="progressbar"
             aria-valuenow={stepIndex + 1} aria-valuemin={1} aria-valuemax={steps.length}>
          {steps.map((label, i) => (
            <React.Fragment key={label + i}>
              {i > 0 && <span className="fc-bar" />}
              <button
                type="button"
                className={`fc-stp ${i < stepIndex ? 'done' : (i === stepIndex ? 'on' : '')}`}
                onClick={() => { if (i <= stepIndex) onStepClick?.(i); }}
              >
                <span className="n">{i < stepIndex ? '✓' : fmtNumber(i + 1)}</span>
                <span>{label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>

        <div className="fc-panel">
          {children}

          <div className="fc-foot">
            <button
              type="button" className="fc-btn fc-btn-go"
              disabled={!canNext || submitting}
              onClick={onNext}
            >
              {submitting ? '…' : (isLast ? (nextLabel || t('cafe_wizard.play_now_final')) : t('common.next'))}
            </button>
            <button
              type="button" className="fc-btn fc-btn-ghost"
              disabled={submitting}
              onClick={onBack}
            >
              {stepIndex === 0 ? t('cafe_wizard.back_to_picker') : t('common.back')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
